import store from '../store';
import { EventBus } from './event-bus-service';

export default class LeftcolResizerService {
  /**
   * All widgets from left column
   * @type {NodeListOf}
   */
  allLeftColWidgets = null;
  /**
   * Is left column expanded or not, by default will be expanded
   * @type {boolean}
   */
  expanded = true;
  /**
   * Widgets whose content will not be hidden
   * @type {string[]}
   */
  excluded = [
    'bto-sb-widget-betting-menu',
    'bto-sb-widget-sportlist-menu'
  ];
  /**
   * Widgets that will be hidden when left col is collapsed
   */
  hideWhenCollapsed = [
    'div.bto-sb-widget-banner',
    'div.bto-sb-widget-match-code',
    'div.bto-sb-element iframe',
  ];
  /**
   * Query to select widgets that will be hidden when left col is collapsed
   */
  hideWhenCollapsedQuery = this.hideWhenCollapsed.join(',');
  /**
   * Check when left column is collapsing
   * @type {number}
   */
  breakWindowWidth = 1420;

  /**
   * should the column collapse on screen resize and smaller screens according to breakWindowWidth
   * @type {boolean}
   */
  autoCollapseOnResize = false;

  /**
   * Is resize called or not
   * @type {boolean}
   */
  resizeCalled = false;

  constructor() {
    this.resizeHandler = this.resizeHandler.bind(this);
    this.leftColToggleHandler = this.leftColToggleHandler.bind(this);
  }

  /**
   * Init all logic here
   */
  init() {
    this.collectWidgets();
    EventBus.$on('leftColToggle', this.leftColToggleHandler);
    this.resizeHandler();
  }

  /**
   * Clear event listeners
   */
  destroy() {
    EventBus.$off('leftColToggle', this.leftColToggleHandler);
    this.setWindowResizeEvents(false);
  }

  /**
   * Handler for leftColToggle event
   * @param {boolean} expanded - Flag if left col is expanded
   */
  leftColToggleHandler(expanded) {
    this.expanded = expanded;
    const col = document.querySelector('div.bto-sb-col-1');
    if (col) {
      if (expanded) {
        col.classList.remove('bto-sb-short-col');
        this.removeDefaultWidgetStyles();
        this.removeWidgetEvents();
      } else {
        this.collectWidgets();
        col.classList.add('bto-sb-short-col');
        this.setDefaultWidgetStyles();
        this.addWidgetEvents();
      }
    }
  }

  /**
   * Get available widgets
   */
  collectWidgets() {
    this.allLeftColWidgets = document.querySelectorAll('div.bto-sb-col-1 .bto-sb-widget:not(.bto-sb-widget-betting-menu)');
  }

  /**
   * Toggle state of left column
   */
  toggleLeftColumn() {
    store.commit('toggleLeftCol');
  }

  /**
   * Loop over widgets and execute callback method
   * @param callback
   */
  loopWidgets(callback) {
    this.allLeftColWidgets.forEach((widget) => {
      callback(widget);
    });
  }

  /**
   * Set default widget heights
   */
  setDefaultWidgetStyles() {
    this.loopWidgets((widget) => {
      if (typeof widget !== 'undefined' && widget) {
        const widgetStyle = window.getComputedStyle(widget);
        this.applyClassStlyes(widget, 'set');
        if (widget.parentNode) {
          const widgetMargin = Number.parseInt(widgetStyle.getPropertyValue('margin-bottom'), 10);
          widget.parentNode.style.height = `${widget.offsetHeight + widgetMargin}px`;
        }
      }
    });
    this.toggleHiddenWidgets();
  }

  /**
   * Remove default widget heights
   */
  removeDefaultWidgetStyles() {
    this.loopWidgets((widget) => {
      if (typeof widget !== 'undefined' && widget.parentNode) {
        widget.parentNode.style.height = null;
        widget.parentNode.style.marginBottom = null;
        this.applyClassStlyes(widget, 'remove');
      }
    });
    this.toggleHiddenWidgets();
  }

  /**
   * Show/Hide widgets specified in hideWhenCollapsed property
   */
  toggleHiddenWidgets() {
    const leftCol = document.querySelector('div.bto-sb-col-1');
    const leftColCollapsed = document.querySelector('.bto-sb-short-col');
    if (leftColCollapsed && leftColCollapsed.querySelector(this.hideWhenCollapsedQuery)) {
      leftCol.querySelectorAll(this.hideWhenCollapsedQuery).forEach(node => {
        node.parentNode.style.display = 'none';
      });
    } else if (!leftColCollapsed && leftCol.querySelector(this.hideWhenCollapsedQuery)) {
      leftCol.querySelectorAll(this.hideWhenCollapsedQuery).forEach(node => {
        node.parentNode.style.display = null;
      });
    }
  }
  /**
   * Reset base widget divs that are hidden by toggleHiddenWidgets
   * We need to do this because Vue reuses the same parent div when changing between layouts
   */
  showLeftWidgets() {
    const leftCol = document.querySelector('div.bto-sb-col-1');
    leftCol.querySelectorAll('.bto-base-widget').forEach(node => {
      node.style.display = null;
    });
  }

  /**
   * Apply class styles
   * @param widget
   * @param displayState
   */
  applyClassStlyes(widget, displayState = 'set') {
    const containsExcludedClass = this.excluded.some((c) => {
      return widget.classList.contains(c);
    });
    if (!containsExcludedClass) {
      const content = widget.querySelector('div.bto-sb-widget-content');
      if (content) {
        if (displayState === 'set') {
          content.setAttribute('displayState', this.getComputedStyle(content, 'display'));
          content.style.display = 'none';
        } else {
          content.style.display = content.getAttribute('displayState');
          content.removeAttribute('displayState');
          content.style.display = null;
        }
      }
    }
  }

  /**
   * Add require events for menus
   */
  addWidgetEvents() {
    this.loopWidgets((widget) => {
      widget.addEventListener('mouseenter', this.handleWidgetEnter, false);
      widget.addEventListener('mouseleave', this.handleWidgetLeave, false);
    });
  }

  /**
   * Remove events
   */
  removeWidgetEvents() {
    this.allLeftColWidgets.forEach((widget) => {
      widget.removeEventListener('mouseenter', this.handleWidgetEnter, false);
      widget.removeEventListener('mouseleave', this.handleWidgetLeave, false);
    });
  }

  /**
   * Handle action when mouse is over widget
   * @param e
   */
  handleWidgetEnter(e) {
    const target = e.target || e.srcElement;
    target.classList.add('bto-sb-current-shown-widget');
  }

  /**
   * Handle action when mouse is leaving widget
   * @param e
   */
  handleWidgetLeave(e) {
    const target = e.target || e.srcElement;
    target.classList.remove('bto-sb-current-shown-widget');
  }

  /**
   * Get style from element
   * @param el
   * @param prop
   */
  getComputedStyle(el, prop) {
    const widgetStyle = window.getComputedStyle(el);
    return widgetStyle.getPropertyValue(prop);
  }

  /**
   * Window resize event
   * @param on
   */
  setWindowResizeEvents(on = true) {
    if (on) {
      window.addEventListener('resize', this.resizeHandler);
    } else {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  /**
   * Resize handler
   */
  resizeHandler() {
    if (!this.autoCollapseOnResize) {
      return;
    }
    if (window.innerWidth <= this.breakWindowWidth) {
      if (!this.resizeCalled) {
        store.commit('toggleLeftCol', true);
        this.resizeCalled = true;
      }
    } else {
      if (this.resizeCalled && !store.state.LayoutModule.leftColExpanded) {
        store.commit('toggleLeftCol', false);
      }
      this.resizeCalled = false;
    }
  }
}
